/* eslint-disable react/no-unknown-property */
import {
  Bounds,
  Bvh,
  Environment,
  Preload,
  useProgress,
} from '@react-three/drei'
import type { EnvironmentProps } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import type { CanvasProps } from '@react-three/fiber'
import React, { Suspense, forwardRef, useImperativeHandle, useRef } from 'react'
import { Mesh, Vector3 } from 'three'

import {
  DEFAULT_AMBIENT_LIGHT_CONTROLS,
  DEFAULT_CANVAS_CONTROLS,
  DEFAULT_ENVIRONMENT_CONTROLS,
  DEFAULT_FREE_FORM_CAMERA_CONTROLS,
  DEFAULT_GL_CONTROLS,
  OutputColorSpaceMap,
  ToneMappingMap,
} from './constants'
import SceneContext from './context'
import type { SceneContextTypes } from './context'
import FirstPersonDragControls from './controls/first-person-drag-controls'
import CameraControls from './controls/orbit-controls'
import type { OrbitControlsRefProps } from './controls/orbit-controls'
import GLTFBuilder, {
  type ParalaxAssetProps,
  type ReflectorProps,
} from './gltf-builder'
import type {
  INTERACTION_HOVER,
  INTERACTION_POINT,
  ModelControlsInterface,
} from './objects/hover-hitbox'
import {
  PerformanceStats,
  VRAMTracker,
  usePerformanceStats,
  useVRAMTracker,
} from './performance-stats'
import type { PerformanceStatsRef } from './performance-stats'
import SceneCompiler from './scene-compiler'
import useStaticModelLoader from './static-model-loader'
import type {
  CameraControl,
  CanvasControl,
  ModelInterface,
  ModelLoader,
} from './types'
import LoadingScreen, { LoaderBg } from './util/loader'

type GLTFNodes = {
  [key: string]: Mesh
}

export interface ModelRefProps extends OrbitControlsRefProps {
  setShowModel: (arg: boolean) => void
  startStaticLoader: (loadableModelId: string) => void
}

export interface ModelProps {
  models: ModelInterface[]
  modelControl: ModelControlsInterface
  color?: string
  getTarget?: (target: Vector3) => void
  getPosition?: (position: Vector3) => void
  detectChange?: (arg: boolean) => void
  getModelStatus?: (arg: boolean) => void
  disableCameraLoadingCntrl?: boolean
  getHighlightedTarget?: (arg: string) => void
  getNodes?: (nodes: GLTFNodes) => void
  enablePan?: boolean
  enableFreeFormCameraControl?: boolean
  interactionMode?: INTERACTION_HOVER | INTERACTION_POINT
  requireActionForHover?: boolean
  enablePerformanceStats?: boolean
  firstPersonMode?: boolean
  hitboxVisible?: boolean
  parallaxAssets?: ParalaxAssetProps
  reflectorControl?: ReflectorProps
  cameraPosition?: Vector3
  cameraTarget?: Vector3
}

const PROGRESS_COMPLETE = 100

const HITBOX_GROUP_NAME = 'Collision_GRP'

const Model = forwardRef<ModelRefProps | undefined, ModelProps>(
  (
    {
      getPosition,
      getTarget,
      color = 'skyblue',
      modelControl,
      models,
      detectChange,
      getModelStatus,
      disableCameraLoadingCntrl = false,
      getHighlightedTarget,
      getNodes,
      enablePan,
      enableFreeFormCameraControl = false,
      interactionMode = 'hover',
      requireActionForHover = true,
      enablePerformanceStats = false,
      firstPersonMode = false,
      hitboxVisible = false,
      parallaxAssets,
      reflectorControl,
      cameraPosition,
      cameraTarget,
    },
    ref
  ) => {
    const { progress, errors } = useProgress()

    const refControl = React.useRef<OrbitControlsRefProps>(null)

    const [showModel, setShowModel] = React.useState(false)
    const [isMoving, setIsMoving] = React.useState(false)
    const [loader, setLoader] = React.useState<ModelLoader>({})
    const [hdr, setHdr] = React.useState('')
    const [activeModelName, setActiveModelName] = React.useState('')
    const [clickTargetPosition, getClickTargetPosition] = React.useState<
      Array<number>
    >([])

    const {
      showLoader: showStaticLoader,
      loaderBg: staticLoaderBg,
      startLoader: startStaticLoader,
    } = useStaticModelLoader({ models })

    const FIRST_PERSON_NEAR_CLIP = 0.0001

    useImperativeHandle(ref, () => ({
      spawnCamera: (arg1: Vector3, arg2: Vector3) =>
        refControl?.current && refControl?.current.spawnCamera(arg1, arg2),
      setPosition: (arg: Vector3) =>
        refControl?.current && refControl?.current.setPosition(arg),
      setTarget: (arg: Vector3) =>
        refControl?.current && refControl?.current.setTarget(arg),
      setShowModel: (arg: boolean) => setShowModel(arg),
      startStaticLoader: (arg: string) => startStaticLoader(arg),
    }))

    React.useEffect(() => {
      const activeModel = models.find((mdl) => mdl.visible)
      if (activeModel) {
        setActiveModelName(activeModel.id)
        setLoader(activeModel.loader)
      }
      if (activeModel?.hdr) {
        setHdr(activeModel.hdr)
      }
    }, [models])

    const derivedActiveModelPosition = React.useMemo(() => {
      const activeModel = models.find((mdl) => mdl.visible)
      if (activeModel) {
        return new Vector3(...activeModel.position)
      }
      return new Vector3(0, 0, 0)
    }, [models])

    const derivedActiveModelTarget = React.useMemo(() => {
      const activeModel = models.find((mdl) => mdl.visible)
      if (activeModel) {
        return new Vector3(...activeModel.target)
      }
      return new Vector3(0, 0, 0)
    }, [models])

    const getActiveModelPosition = cameraPosition ?? derivedActiveModelPosition
    const getActiveModelTarget = cameraTarget ?? derivedActiveModelTarget

    const activeModelCanvasControl = React.useMemo<CanvasControl | undefined>(
      () => models.find((model) => model.visible)?.canvas,
      [models, enableFreeFormCameraControl]
    )

    const activeModel = React.useMemo(
      () => models.find((model) => model.visible),
      [models]
    )

    const activeModelCameraControl = React.useMemo<
      CameraControl | undefined
    >(() => {
      if (enableFreeFormCameraControl) {
        return DEFAULT_FREE_FORM_CAMERA_CONTROLS
      }

      return activeModel?.cameraControl
    }, [activeModel, enableFreeFormCameraControl])

    const performanceStatsRef = useRef<PerformanceStatsRef>(null)
    const performanceStats = usePerformanceStats()
    const { activeModelGLTFData, setGLTFData } = useVRAMTracker({
      activeModel,
    })

    const getActiveModel = React.useMemo(
      () =>
        models
          .filter((model) => model.visible)
          .map((model) => (
            <GLTFBuilder
              key={`gltfbuilder-${model.id}`}
              gltfSrc={model.gltfSrc}
              modelControl={modelControl}
              getHighlightedTarget={getHighlightedTarget}
              visible={model.visible}
              getNodes={getNodes}
              modelLoaded={setShowModel}
              interactionMode={interactionMode}
              disableCameraLoadingCntrl={disableCameraLoadingCntrl}
              isMoving={isMoving}
              requireActionForHover={requireActionForHover}
              onGLTFDataFound={
                enablePerformanceStats ? setGLTFData(model.id) : undefined
              }
              firstPersonMode={firstPersonMode}
              getClickTargetPosition={getClickTargetPosition}
              parallaxAssets={parallaxAssets}
              reflectorCtrls={reflectorControl}
            />
          )),
      [
        models,
        modelControl,
        isMoving,
        hitboxVisible,
        parallaxAssets,
        reflectorControl,
        firstPersonMode,
      ]
    )

    const gl = React.useMemo<CanvasProps['gl']>(() => {
      const activeGlControl = activeModel?.glControl
      const toneMapping =
        (activeGlControl?.toneMapping &&
          ToneMappingMap[activeGlControl.toneMapping]) ||
        DEFAULT_GL_CONTROLS.toneMapping
      const toneMappingExposure =
        activeGlControl?.toneMappingExposure ??
        DEFAULT_GL_CONTROLS.toneMappingExposure
      const outputColorSpace =
        (activeGlControl?.outputColorSpace &&
          OutputColorSpaceMap[activeGlControl.outputColorSpace]) ||
        DEFAULT_GL_CONTROLS.outputColorSpace
      const precision = activeGlControl?.precision ?? undefined

      return {
        ...activeGlControl,
        toneMapping,
        toneMappingExposure,
        outputColorSpace,
        precision,
      }
    }, [activeModel?.glControl])

    const environment = React.useMemo<EnvironmentProps>(() => {
      const activeEnvironmentControl = activeModel?.environmentControl
      let environmentIntensity =
        activeEnvironmentControl?.environmentIntensity ??
        DEFAULT_ENVIRONMENT_CONTROLS.environmentIntensity
      const environmentRotation =
        activeEnvironmentControl?.environmentRotation ?? undefined
      const backgroundIntensity =
        activeEnvironmentControl?.backgroundIntensity ??
        DEFAULT_ENVIRONMENT_CONTROLS.backgroundIntensity
      const backgroundBlurriness =
        activeEnvironmentControl?.backgroundBlurriness ??
        DEFAULT_ENVIRONMENT_CONTROLS.backgroundBlurriness
      const backgroundRotation =
        activeEnvironmentControl?.backgroundRotation ?? undefined
      const resolution = activeEnvironmentControl?.resolution ?? undefined

      let { background }: EnvironmentProps = DEFAULT_ENVIRONMENT_CONTROLS

      if (activeEnvironmentControl?.disableBackground !== undefined) {
        background = !activeEnvironmentControl?.disableBackground
      }
      if (activeEnvironmentControl?.backgroundOnly) {
        background = 'only'
      }
      if (
        activeEnvironmentControl?.backgroundOnly &&
        activeEnvironmentControl?.disableBackground
      ) {
        background = false
        environmentIntensity = 0
      }

      return {
        environmentIntensity,
        environmentRotation,
        backgroundIntensity,
        backgroundBlurriness,
        backgroundRotation,
        background,
        resolution,
      }
    }, [activeModel?.environmentControl])

    const ambientLight = React.useMemo(() => {
      const ambientLightControl = activeModel?.ambientLightControl
      return {
        active:
          ambientLightControl?.active ?? DEFAULT_AMBIENT_LIGHT_CONTROLS.active,
        intensity:
          ambientLightControl?.intensity ??
          DEFAULT_AMBIENT_LIGHT_CONTROLS.intensity,
        color:
          ambientLightControl?.color ?? DEFAULT_AMBIENT_LIGHT_CONTROLS.color,
      }
    }, [activeModel?.ambientLightControl])

    React.useEffect(() => {
      if (getModelStatus) {
        getModelStatus(showModel)
      }
    }, [showModel])

    React.useEffect(() => {
      if (detectChange) {
        setShowModel(false)
        detectChange(false)
      }
    }, [activeModelName])

    const hasErrorMatch = (
      rendererErrors: string[],
      loadedModels: ModelInterface[]
    ) =>
      rendererErrors.some((err) =>
        loadedModels.some((model) => model.hdr === err || model.gltfSrc === err)
      )

    React.useEffect(() => {
      if (hasErrorMatch(errors, models)) {
        getModelStatus?.(true)
        return
      }
      getModelStatus?.(progress === PROGRESS_COMPLETE)
    }, [getModelStatus, progress, errors, models])

    const sceneContext: SceneContextTypes = React.useMemo(
      () => ({
        hitBoxGroupName: HITBOX_GROUP_NAME,
        hitboxVisible,
        disablePuck: false,
      }),
      [hitboxVisible]
    )

    return (
      <Suspense fallback={<LoadingScreen {...loader} />}>
        {showStaticLoader && staticLoaderBg && (
          <div className="relative h-full w-full">
            <LoaderBg bg={staticLoaderBg} />
          </div>
        )}
        {enablePerformanceStats && (
          <PerformanceStats
            ref={performanceStatsRef}
            containerClassName="absolute left-5 bottom-5 z-[1]"
            isPanelVisible={performanceStats.isPanelVisible}
            handleTogglePanel={performanceStats.handleTogglePanel}
          />
        )}
        <Canvas
          shadows
          camera={{
            position: getActiveModelPosition,
            near:
              activeModelCanvasControl?.near ?? DEFAULT_CANVAS_CONTROLS.near,
            far: activeModelCanvasControl?.far ?? DEFAULT_CANVAS_CONTROLS.far,
          }}
          gl={gl}
        >
          <Preload all />
          {performanceStats.isPanelVisible && (
            <VRAMTracker
              gltfData={activeModelGLTFData}
              onUpdate={performanceStatsRef.current?.setVRAM}
            />
          )}
          {ambientLight.active && (
            <ambientLight
              intensity={ambientLight.intensity}
              color={ambientLight.color}
            />
          )}
          <Bounds fit={false} observe>
            <Bvh>
              <SceneContext.Provider value={sceneContext}>
                {firstPersonMode ? (
                  <FirstPersonDragControls
                    initPosition={getActiveModelPosition}
                    initTarget={getActiveModelTarget}
                    updatePosition={clickTargetPosition}
                    getPosition={getPosition}
                    enablePan={enablePan}
                    getTarget={getTarget}
                  />
                ) : (
                  <CameraControls
                    ref={refControl}
                    initPosition={getActiveModelPosition}
                    initTarget={getActiveModelTarget}
                    getPosition={getPosition}
                    detectChange={detectChange}
                    getTarget={getTarget}
                    enablePan={enablePan ?? activeModelCameraControl?.enablePan}
                    isCameraLoaded={setShowModel}
                    isMoving={setIsMoving}
                    disableCameraLoadingCntrl={disableCameraLoadingCntrl}
                    maxDistance={activeModelCameraControl?.maxDistance}
                    minDistance={activeModelCameraControl?.minDistance}
                    maxPolarAngle={activeModelCameraControl?.maxPolarAngle}
                    minPolarAngle={activeModelCameraControl?.minPolarAngle}
                    near={
                      firstPersonMode
                        ? FIRST_PERSON_NEAR_CLIP
                        : activeModelCanvasControl?.near
                    }
                    far={activeModelCanvasControl?.far}
                  />
                )}
                {getActiveModel}
                <SceneCompiler />
              </SceneContext.Provider>
              <color attach="background" args={[color]} />
              {hdr && (
                <Environment
                  files={hdr}
                  environmentIntensity={environment.environmentIntensity}
                  environmentRotation={environment.environmentRotation}
                  background={environment.background}
                  backgroundIntensity={environment.backgroundIntensity}
                  backgroundBlurriness={environment.backgroundBlurriness}
                  backgroundRotation={environment.backgroundRotation}
                  resolution={environment.resolution}
                />
              )}
            </Bvh>
          </Bounds>
        </Canvas>
      </Suspense>
    )
  }
)

export default Model
